/**
 * What the agent is told the analyst is looking at on a dashboard.
 *
 * The rows are trimmed here as well as on the server. The server caps what
 * reaches the prompt — it has to, since nothing trims that block against the
 * token budget — but a refreshed table widget can hold five hundred rows, and
 * a dashboard of them would mean posting a megabyte of JSON on every message
 * for the server to throw most of away. So the bulk is dropped before it goes
 * over the wire, and the server's caps stay as the guarantee.
 *
 * `selected_widget_ids` is the whole point of the shape: widgets the analyst
 * adds to the chat are read as what an unqualified question is about, while
 * every other widget stays in as background so comparisons still work.
 */

/** Generous next to the server's caps, so the server decides the real limit. */
const SELECTED_ROWS = 40;
const OTHER_ROWS = 8;

function trimPayload(payload: any, added: boolean): any {
  if (!payload || payload.type !== 'table_result') return payload;
  const data = payload.data || {};
  const rows: unknown[][] = data.preview_rows || [];
  const cap = added ? SELECTED_ROWS : OTHER_ROWS;
  if (rows.length <= cap) return payload;
  return { ...payload, data: { ...data, preview_rows: rows.slice(0, cap) } };
}

export interface DashboardContextWidget {
  widget_id: string;
  title: string;
  rendered_at?: string | null;
}

export function buildDashboardSurfaceContext(params: {
  dashboardId: string;
  name: string;
  widgets: DashboardContextWidget[];
  /** Each widget's rendered payload, keyed by widget id. */
  specs: Record<string, any>;
  /** Widgets the analyst has added to the chat. */
  selectedWidgetIds: string[];
  lastUpdated?: string | null;
}): Record<string, unknown> {
  const { dashboardId, name, widgets, specs, selectedWidgetIds, lastUpdated } = params;

  return {
    type: 'dashboard',
    dashboard_id: dashboardId,
    name,
    last_updated: lastUpdated ?? null,
    selected_widget_ids: selectedWidgetIds,
    widgets: widgets
      // A widget whose data has not loaded yet has nothing to say about itself.
      .filter((w) => specs[w.widget_id])
      .map((w) => ({
        widget_id: w.widget_id,
        title: w.title,
        rendered_at: w.rendered_at ?? null,
        payload: trimPayload(specs[w.widget_id], selectedWidgetIds.includes(w.widget_id)),
      })),
  };
}
